import React from 'react'
import { Alert, Container } from '@mui/material'

import useRouter from '../../hooks/useRouter'
import BrandDashboard from './BrandDashboard'
import EntertainmentDashboard from './EntertainmentDashboard'
import ProductLineDashboard from './ProductLineDashboard'
import CorporateDashboard from './CorporateDashboard'
import SportsDashboard from './SportsDashboard'
import AllProductsDashboard from './AllProductsDashboard'

/**
 * Slug → the page that renders it.
 *
 * WHY AN EXPLICIT MAP AND NOT A GENERIC PAGE. A single component reading the
 * slug and rendering one arrangement would work today, because all five
 * departments arrange identically — and it would be exactly the shape that
 * makes divergence expensive, since the first department to want something
 * different would have to be special-cased inside it. Five entries pointing at
 * five files means the first divergence edits one file and touches nothing
 * else. That is the whole §5c bet, expressed as a lookup table.
 *
 * The slugs are the manifest's (`products:` in sky-sprinklr.yaml). They are
 * duplicated here rather than derived from the catalogue at runtime, because a
 * route has to resolve to a component synchronously, before any fetch. The
 * backend 404s an unknown slug and so does this map, so the two cannot silently
 * disagree about which departments exist — they can only disagree loudly.
 */
const PRODUCT_PAGES: Record<string, React.ComponentType> = {
  brand: BrandDashboard,
  entertainment: EntertainmentDashboard,
  product: ProductLineDashboard,
  corporate: CorporateDashboard,
  sports: SportsDashboard,
  // The admin view is a product like any other (§0b.5) — no separate route
  // group, no second scope middleware, and so no special case here either.
  // Whether the caller may OPEN it is decided server-side; this map only says
  // which component a slug renders.
  all: AllProductsDashboard,
}

/**
 * The `/sky-social-media/:product` route.
 *
 * An unknown slug is a flat message, not a redirect to the chooser: a mistyped
 * or retired URL should say so, because silently landing somewhere else is how
 * a bookmarked dashboard appears to have quietly changed department.
 */
export const SkyProductRoute: React.FC = () => {
  const router = useRouter()
  const slug = String(router.params.product || '').trim().toLowerCase()
  const Page = PRODUCT_PAGES[slug]

  if (!Page) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="warning">
          There is no dashboard called “{slug || 'unknown'}”. Pick one from the Social Media page.
        </Alert>
      </Container>
    )
  }

  return <Page />
}

export { PRODUCT_PAGES }
export default SkyProductRoute
